import { VideoEventCode } from "../enums/VideoEventCode";
import { VideoReviewStatus } from "../enums/VideoReviewStatus.enum";
import { VideoStatus } from "../enums/VideoStatus";
import { IVideoEntry } from "../models/IVideoEntry";

export class VideoEntryUtils {
    static isEventAndActive(v: IVideoEntry): boolean {
        return v.EventCode != VideoEventCode.UserRequested && v.Status != VideoStatus.Archived;
    }

    static isUnseen(v: IVideoEntry) {
        return v.Status == VideoStatus.Downloaded && !VideoEntryUtils.isSeen(v);
    }

    static isSeen(v: IVideoEntry): boolean {
        return v.Status == VideoStatus.Downloaded && v.MetaData && v.MetaData.Watched.length > 0;
    }

    static isRequested(videoEntry: IVideoEntry): boolean {
        const requestedStates: VideoStatus[] = [
            VideoStatus.UploadRequested,
            VideoStatus.UserUploadRequested
        ]

        return requestedStates.includes(videoEntry.Status);
    }

    static isDownloading(videoEntry: IVideoEntry): boolean {
        const downloadingStates: VideoStatus[] = [
            VideoStatus.Uploading,
            VideoStatus.Uploaded,
            VideoStatus.PartiallyUploaded
        ];

        return downloadingStates.includes(videoEntry.Status);
    }

    static isDownloaded(videoEntry: IVideoEntry): boolean {
        if (this.hasNoFootage(videoEntry)) {
            videoEntry.Status = VideoStatus.NoFootageAvailable;
        }

        const downloadedStates: VideoStatus[] = [
            VideoStatus.Downloaded,
            VideoStatus.PartiallyDownloaded
        ];

        return downloadedStates.includes(videoEntry.Status) || (videoEntry.Status === VideoStatus.Archived && !!videoEntry.DateDownloaded);
    }

    static isFaceblurring(videoEntry: IVideoEntry): boolean {
        const faceblurringStates: VideoStatus[] = [
            VideoStatus.AwaitingIndexing,
            VideoStatus.AwaitingRedaction,
            VideoStatus.RedactionCompleted,
            VideoStatus.IndexingInProgress,
            VideoStatus.RedactionInProgress,
            VideoStatus.AwaitingVideoMute
        ]

        return faceblurringStates.includes(videoEntry.Status);
    }

    static isDownloadFailed(videoEntry: IVideoEntry): boolean {
        if (this.hasNoFootage(videoEntry)) {
            videoEntry.Status = VideoStatus.NoFootageAvailable;
        }

        const failedStates: VideoStatus[] = [
            VideoStatus.Error,
            VideoStatus.NoFootageAvailable,
            VideoStatus.NoFilesAvailable,
            VideoStatus.HardwareDeleted,
            VideoStatus.Cancelled,
            VideoStatus.Expired
        ];

        return failedStates.includes(videoEntry.Status);
    }

    static isDeleted(videoEntry: IVideoEntry): boolean {
        const deletedStates: VideoStatus[] = [
            VideoStatus.MediaDeleted,
            VideoStatus.MediaPendingDeletion
        ];

        return deletedStates.includes(videoEntry.Status);
    }

    static isDownloadedOrDownloading(videoEntry: IVideoEntry): boolean {
        return VideoEntryUtils.isDownloaded(videoEntry) || VideoEntryUtils.isDownloading(videoEntry);
    }

    static needsReview(v: IVideoEntry): boolean {
        if (!VideoEntryUtils.isDownloaded(v)) {
            return false;
        }

        return v.reviewStatus === VideoReviewStatus.NeedsReview;
    }

    static getVideoDownloadProgress(videoEvent: IVideoEntry): number {
        return videoEvent.Status == VideoStatus.Downloaded ? 100 : videoEvent.MetaData.DownloadProgress;
    }

    static hasNoFootage(videoEntry: IVideoEntry): boolean {
        if (videoEntry.Status == VideoStatus.PartiallyDownloaded && !videoEntry.Mp4VideoFileGuid) {
            // There are some instances of videos that show as PartiallyDownloaded but have no associated files
            return true;
        }

        const noFootageStates: VideoStatus[] = [
            VideoStatus.NoFilesAvailable,
            VideoStatus.NoFootageAvailable
        ];

        return noFootageStates.includes(videoEntry.Status);
    }

    static isRequestComplete(videoEntry: IVideoEntry): boolean {
        return VideoEntryUtils.hasNoFootage(videoEntry) || VideoEntryUtils.isDownloadFailed(videoEntry) || VideoEntryUtils.isDownloaded(videoEntry);
    }
}
